// @spec GHG-STR-003
/**
 * API client functions for GHG facilities
 */

import type {
  CreateFacilityInput,
  Facility,
  FacilityListFilters,
  FacilityListResponse,
  FacilitySummary,
  UpdateFacilityInput,
} from '../types';

import { createApiClient } from '@/lib/api-client';

/**
 * Fetch facilities for an organization with optional filtering.
 */
export async function fetchFacilities(
  getToken: () => Promise<string | null>,
  organizationId: string,
  filters?: FacilityListFilters
): Promise<FacilityListResponse> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.facilityType) params.set('facilityType', filters.facilityType);
  if (filters?.legalEntityId) params.set('legalEntityId', filters.legalEntityId);
  if (filters?.derivedBoundaryIncluded !== undefined) {
    params.set('derivedBoundaryIncluded', String(filters.derivedBoundaryIncluded));
  }
  if (filters?.boundaryIncluded !== undefined) {
    params.set('boundaryIncluded', String(filters.boundaryIncluded));
  }
  if (filters?.boundaryOverrideMode) {
    params.set('boundaryOverrideMode', filters.boundaryOverrideMode);
  }
  if (filters?.search) params.set('search', filters.search);
  if (filters?.page) params.set('page', String(filters.page));
  if (filters?.pageSize) params.set('pageSize', String(filters.pageSize));

  const queryString = params.toString();
  const url = `/organizations/${organizationId}/ghg/facilities${queryString ? `?${queryString}` : ''}`;

  return apiClient.get<FacilityListResponse>(url);
}

/**
 * Fetch a single facility by ID.
 */
export async function fetchFacility(
  getToken: () => Promise<string | null>,
  organizationId: string,
  facilityId: string
): Promise<Facility> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  return apiClient.get<Facility>(`/organizations/${organizationId}/ghg/facilities/${facilityId}`);
}

/**
 * Create a new facility.
 */
export async function createFacility(
  getToken: () => Promise<string | null>,
  organizationId: string,
  data: CreateFacilityInput
): Promise<Facility> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  return apiClient.post<Facility>(`/organizations/${organizationId}/ghg/facilities`, data);
}

/**
 * Update an existing facility.
 */
export async function updateFacility(
  getToken: () => Promise<string | null>,
  organizationId: string,
  facilityId: string,
  data: UpdateFacilityInput
): Promise<Facility> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  return apiClient.put<Facility>(
    `/organizations/${organizationId}/ghg/facilities/${facilityId}`,
    data
  );
}

/**
 * Delete a facility (soft delete).
 */
export async function deleteFacility(
  getToken: () => Promise<string | null>,
  organizationId: string,
  facilityId: string
): Promise<void> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  await apiClient.delete(`/organizations/${organizationId}/ghg/facilities/${facilityId}`);
}

/**
 * Fetch facility summary with activity grouping counts.
 */
export async function fetchFacilitySummary(
  getToken: () => Promise<string | null>,
  organizationId: string,
  facilityId: string
): Promise<FacilitySummary> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  return apiClient.get<FacilitySummary>(
    `/organizations/${organizationId}/ghg/facilities/${facilityId}/summary`
  );
}
